import {
  Cloud,
  Settings,
  X,
  Globe,
  AlertCircle,
  LogIn,
  UploadCloud,
  Check,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { useGoogleDrive } from '@/shared/providers/GoogleDriveProvider';
import { useSyncStore } from '@/features/sync/stores/useSyncStore';
import { useUserPreferences } from '@/features/pages/hooks/useUserPreferences';
import { useState, useEffect } from 'react';
import { Separator } from '@radix-ui/react-dropdown-menu';
import { useSyncContext } from '@/features/sync/context/SyncProvider';

const AppSettings = ({ onClose }: { onClose: () => void }) => {
  const { lastSyncedAt } = useGoogleDrive();
  const { isConnected, user, isSyncing, handleGoogleDriveConnect, syncWithGoogleDrive } =
    useGoogleDrive();
  const { signIn } = useSyncContext();

  // Auth state from sync store
  const { user: authUser } = useSyncStore();

  // User preferences hook
  const {
    preferences,
    loading: preferencesLoading,
    saving: preferencesSaving,
    deleting: preferencesDeleting,
    error: preferencesError,
    updatePreferences,
    deletePreferences,
    clearError,
    showSubmittedSuccess,
  } = useUserPreferences();

  // Single form state object
  const [formData, setFormData] = useState({
    displayName: '',
    subdomain: '',
  });

  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load initial values when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setFormData({
        displayName: preferences.displayName,
        subdomain: preferences.subdomain,
      });
    }
  }, [preferences]);

  const handleSavePreferences = async () => {
    if (!authUser) return;

    // Clear any previous errors
    clearError();

    // Validate required fields
    if (!formData.displayName.trim() || !formData.subdomain.trim()) {
      return; // Error will be shown by the hook
    }

    try {
      await updatePreferences({
        displayName: formData.displayName.trim(),
        subdomain: formData.subdomain.trim(),
      });
    } catch (error) {
      // Error is already set by the hook
      console.error('Failed to save preferences:', error);
    }
  };

  const handleDeletePreferences = async () => {
    try {
      await deletePreferences();
      setFormData({ displayName: '', subdomain: '' });
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete preferences:', error);
    }
  };

  const canSavePreferences =
    authUser &&
    formData.displayName.trim() &&
    formData.subdomain.trim() &&
    !preferencesSaving &&
    !preferencesDeleting;

  // Determine if this is a create or update operation
  // Only consider it "new" if we've finished loading and preferences is still null
  const buttonText = preferencesSaving
    ? 'Saving Profile...'
    : showSubmittedSuccess
      ? 'Saved!'
      : 'Save Profile';

  return (
    <Card className="w-full pb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">
          <div className="flex items-center justify-center space-x-2 text-2xl">
            <Settings className="h-6 w-6" />
            <span className="pb-1">Settings</span>
          </div>
        </CardTitle>
        <Button
          onClick={onClose}
          variant="ghost"
          className="h-12 w-12 hover:bg-transparent rounded-full"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {/* Google Drive Backup */}
        <div className="border mt-4 rounded-md bg-secondary p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <UploadCloud className="h-5 w-5" />
                <p className="text-lg font-medium">Google Drive Backup</p>
              </div>
              <p className="text-sm">Backup/Restore your notes from your Google Drive </p>
              <p className="text-sm text-gray-500">{user?.email}</p>
              {lastSyncedAt && (
                <p className="text-xs text-gray-400">
                  Last synced: {new Date(lastSyncedAt).toLocaleString()}
                </p>
              )}
            </div>
            {isConnected ? (
              <Button
                onClick={syncWithGoogleDrive}
                disabled={isSyncing}
                variant="outline"
                className="text-green-600 border-green-600"
              >
                <Cloud className="mr-2 h-4 w-4" />
                {isSyncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            ) : (
              <Button variant={'outline'} onClick={handleGoogleDriveConnect}>
                Connect
              </Button>
            )}
          </div>
        </div>

        <Separator className="my-6 bg-red-500" />

        <div className="space-y-12">
          {/* User Profile Settings */}
          <div className="space-y-4 border rounded-md bg-secondary p-4">
            <div>
              <div className="flex items-center space-x-2">
                <Globe className="h-5 w-5" />
                <h3 className="text-lg font-medium">Public Domain Settings</h3>
              </div>
              {authUser ? (
                <p>
                  Your public collections (if any) will be available at:{' '}
                  <a
                    target="_blank"
                    onClick={e => {
                      if (preferencesError && formData.subdomain === '') {
                        e.preventDefault();
                      }
                    }}
                    href={`https://${formData.subdomain}.${window.location.hostname}`}
                    className="bg-background font-mono p-1 rounded-sm"
                  >
                    {formData.subdomain || 'subdomain'}.{window.location.hostname}
                  </a>
                </p>
              ) : (
                <p>Use this to create a custom subdomain for your public collections</p>
              )}
            </div>

            {!authUser ? (
              <Button onClick={signIn} className="flex items-center space-x-2">
                <LogIn className="h-4 w-4" />
                <p className="text-sm">Login to configure your subdomain</p>
              </Button>
            ) : (
              <div className="space-y-4">
                {preferencesError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center space-x-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <p className="text-sm">{preferencesError}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">Display Name</label>
                  <Input
                    placeholder="Enter your display name"
                    value={formData.displayName}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, displayName: e.target.value }));
                    }}
                    disabled={preferencesLoading || preferencesSaving}
                  />
                  <p className="text-xs text-gray-500">
                    This will be shown on your public collections
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Subdomain</label>
                  <Input
                    placeholder="subdomain"
                    value={formData.subdomain}
                    onChange={e => {
                      const cleanValue = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                      setFormData(prev => ({ ...prev, subdomain: cleanValue }));
                    }}
                    disabled={preferencesLoading || preferencesSaving}
                  />
                </div>

                <Button
                  onClick={handleSavePreferences}
                  disabled={!canSavePreferences || showSubmittedSuccess}
                  className="w-full flex gap-2"
                >
                  {buttonText}
                  {showSubmittedSuccess && <Check className="text-green-500 h-4 w-4" />}
                </Button>

                {/* Delete Button - only show if preferences exist */}
                {preferences && (
                  <div className="space-y-2">
                    {!showDeleteConfirm ? (
                      <Button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                        }}
                        variant="outline"
                        className="w-full text-destructive hover:text-red-600"
                        disabled={preferencesDeleting || preferencesSaving}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Subdomain
                      </Button>
                    ) : (
                      <div className="space-y-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-800">
                          Are you sure you want to delete your subdomain? This will remove your
                          subdomain and all public access to your collections.
                        </p>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => {
                              void handleDeletePreferences();
                            }}
                            variant="destructive"
                            size="sm"
                            disabled={preferencesDeleting}
                            className="flex-1"
                          >
                            {preferencesDeleting ? 'Deleting...' : 'Yes, Delete'}
                          </Button>
                          <Button
                            onClick={() => {
                              setShowDeleteConfirm(false);
                            }}
                            variant="outline"
                            size="sm"
                            disabled={preferencesDeleting}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppSettings;
